import dayjs from "dayjs";
import type { Shift } from "../types/Shift";
import { recentPastShifts } from "./rollover";

export interface UpcomingShiftsDayGroup {
	/** "Today" | "Tomorrow" | "Mon, Mar 5" — the display label for the day bucket. */
	label: string;
	/** Local-time start-of-day for the bucket; stable React key. */
	dayStartMs: number;
	shifts: Shift[];
}

function startOfDayMs(d: Date | dayjs.Dayjs): number {
	return dayjs(d).startOf("day").valueOf();
}

/** How many days past today the upcoming list extends: today + the next two days (today,
 *  tomorrow, day-after-tomorrow). Beyond this a shift is out of the scheduling window. */
export const UPCOMING_WINDOW_DAYS = 2;

/**
 * Shifts scheduled from today through {@link UPCOMING_WINDOW_DAYS} days out (today, tomorrow,
 * day-after-tomorrow), keyed on Sched Date, sorted ascending. Shifts without a Sched Date are
 * skipped — MC reads only 'Sched Date' and never falls back to Shift Start/End Final, so a
 * far-future 'Shift End Final' can't drag a past-scheduled shift into the upcoming list.
 * Ties on the day are stable in insertion order.
 */
export function upcomingShifts(shifts: Shift[], now: Date): Shift[] {
	const todayStart = dayjs(now).startOf("day");
	const firstDay = todayStart.valueOf();
	const lastDay = todayStart.add(UPCOMING_WINDOW_DAYS, "day").valueOf();
	const inWindow: Array<{ shift: Shift; idx: number; day: number }> = [];
	for (let i = 0; i < shifts.length; i++) {
		const s = shifts[i];
		if (!s.scheduledDate) continue;
		const day = startOfDayMs(s.scheduledDate);
		if (day < firstDay || day > lastDay) continue;
		inWindow.push({ shift: s, idx: i, day });
	}
	inWindow.sort((a, b) => a.day - b.day || a.idx - b.idx);
	return inWindow.map((w) => w.shift);
}

function labelForDay(dayStart: dayjs.Dayjs, today: dayjs.Dayjs): string {
	const tomorrow = today.add(1, "day");
	if (dayStart.isSame(today, "day")) return "Today";
	if (dayStart.isSame(tomorrow, "day")) return "Tomorrow";
	const sameYear = dayStart.isSame(today, "year");
	return dayStart.format(sameYear ? "ddd, MMM D" : "ddd, MMM D, YYYY");
}

/**
 * Group shifts by the local day of their Sched Date (the same axis the list windows on, so a
 * card never lands under a day it wasn't windowed into). Preserves the input order; each
 * group's `shifts` list keeps the input order. Shifts without a Sched Date fall into the
 * "Today" bucket so they don't silently vanish — but the scheduling list only ever feeds this
 * Sched-Date-bearing shifts (see {@link schedulingListShifts}), so that fallback is defensive.
 */
export function groupShiftsByDay(shifts: Shift[], now: Date): UpcomingShiftsDayGroup[] {
	const today = dayjs(now).startOf("day");
	const buckets = new Map<number, UpcomingShiftsDayGroup>();
	const order: number[] = [];
	for (const shift of shifts) {
		const day = shift.scheduledDate ? dayjs(shift.scheduledDate).startOf("day") : today;
		const key = day.valueOf();
		let bucket = buckets.get(key);
		if (!bucket) {
			bucket = { label: labelForDay(day, today), dayStartMs: key, shifts: [] };
			buckets.set(key, bucket);
			order.push(key);
		}
		bucket.shifts.push(shift);
	}
	return order.map((key) => buckets.get(key) as UpcomingShiftsDayGroup);
}

/** How the shift list buckets its cards: by start day or by operator. */
export type ShiftGroupBy = "day" | "operator";

export const NO_OPERATOR_GROUP_LABEL = "(no operator)";

export interface UpcomingShiftsOperatorGroup {
	/** `operatorId`, or {@link NO_OPERATOR_GROUP_LABEL} for the operator-less bucket; stable React key. */
	key: string;
	/** Operator display name (or the no-operator label). */
	label: string;
	shifts: Shift[];
}

/**
 * Group shifts by `operatorId` (display name may collide across operators — MC keyed on the name
 * and merged them). Groups sort alphabetically by label; shifts without an operator bucket under
 * "(no operator)", pinned last (MC dropped them from the list entirely). Within a group the input
 * order is preserved — callers pass the `upcomingShifts` output, which is already startDate-sorted.
 */
export function groupShiftsByOperator(shifts: Shift[]): UpcomingShiftsOperatorGroup[] {
	const buckets = new Map<string, UpcomingShiftsOperatorGroup>();
	for (const shift of shifts) {
		const key = shift.operatorId || NO_OPERATOR_GROUP_LABEL;
		let bucket = buckets.get(key);
		if (!bucket) {
			bucket = { key, label: shift.operatorId ? shift.operator || key : NO_OPERATOR_GROUP_LABEL, shifts: [] };
			buckets.set(key, bucket);
		}
		bucket.shifts.push(shift);
	}
	const groups = Array.from(buckets.values());
	return groups.sort((a, b) => {
		if (a.key === NO_OPERATOR_GROUP_LABEL) return 1;
		if (b.key === NO_OPERATOR_GROUP_LABEL) return -1;
		return a.label.localeCompare(b.label);
	});
}

/**
 * The scheduling list's shift universe — MC parity (recent-parity plan): MC has no
 * recent/past section; a past shift stays in its one date-grouped list exactly while a
 * still-relevant (unsampled) job references it, fully editable, and disappears once the
 * work is done. Here: recent-past shifts (the 7-day window stands in for MC's Airtable
 * view filter) with at least one loaded-and-unsampled job, ascending, then the upcoming
 * shifts (today through the day after tomorrow) — all keyed on Sched Date. A past shift
 * whose jobs are not loaded yet stays hidden — it pops in when the prefetch resolves
 * rather than flickering out.
 */
export function schedulingListShifts(
	shifts: Shift[],
	now: Date,
	unsampledByJobId: ReadonlyMap<string, boolean>,
): Shift[] {
	const pastWithWork = recentPastShifts(shifts, now)
		.filter((s) => s.jobIds.some((id) => unsampledByJobId.get(id) === true))
		.reverse(); // recentPastShifts sorts most-recent-first; the list reads ascending
	return [...pastWithWork, ...upcomingShifts(shifts, now)];
}
